import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionHeading } from "./SectionHeading";
import { trackEvent } from "@/lib/umami";

const EXAMPLE_ADDRESSES = ["github@", "k7m2x9@", "newsletters@", "amber-otter-42@"];

/** Example of the random subdomain a shared (mailfinity.email) domain gets. */
const SHARED_EXAMPLE = "calm-otter-4821.mailfinity.email";

export function HowItWorks() {
  const { t } = useTranslation("landing");

  const steps = [
    {
      n: "01",
      key: "domain",
      visual: (
        <div className="rounded-lg bg-muted px-3.5 py-3 font-mono text-[13.5px]">
          mail.northfield.dev
        </div>
      ),
    },
    {
      n: "02",
      key: "dns",
      visual: (
        <div className="flex flex-col gap-1.5">
          {(
            [
              ["MX", "found"],
              ["TXT", "found"],
              ["TXT", "checking"],
            ] as const
          ).map(([type, state], i) => (
            <div
              key={i}
              className="flex items-center justify-between rounded-lg bg-muted px-3 py-2"
            >
              <span className="font-mono text-xs text-muted-foreground">{type}</span>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
                  state === "found" ? "bg-protect-soft text-protect" : "bg-warn-soft text-warn"
                }`}
              >
                <span className="size-1.5 rounded-full bg-current" />
                {t(`how.steps.dns.${state}`)}
              </span>
            </div>
          ))}
        </div>
      ),
    },
    {
      n: "03",
      key: "use",
      visual: (
        <div className="flex flex-wrap gap-1.5">
          {EXAMPLE_ADDRESSES.map((a) => (
            <span key={a} className="rounded-full bg-muted px-2.5 py-1 font-mono text-xs">
              {a}
            </span>
          ))}
        </div>
      ),
    },
  ];

  return (
    <section id="how" className="mx-auto max-w-[1200px] scroll-mt-24 px-5 pt-24 md:px-10 md:pt-32">
      <SectionHeading
        eyebrow={t("how.eyebrow")}
        title={t("how.title")}
        subtitle={t("how.subtitle")}
      />
      <ol className="mt-10 grid gap-5 md:grid-cols-3">
        {steps.map((step) => (
          <li
            key={step.key}
            className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-6 shadow-card"
          >
            <span className="font-mono text-xs font-medium text-primary">{step.n}</span>
            <h3 className="text-lg font-semibold tracking-[-0.02em]">
              {t(`how.steps.${step.key}.title`)}
            </h3>
            <p className="text-[15px] leading-relaxed text-muted-foreground">
              {t(`how.steps.${step.key}.body`)}
            </p>
            <div className="mt-auto pt-2">{step.visual}</div>
          </li>
        ))}
      </ol>

      {/* Second path: no domain needed, a free subdomain of mailfinity.email. */}
      <div
        id="free"
        className="mt-5 grid scroll-mt-28 gap-6 rounded-2xl border border-border bg-card p-6 shadow-card md:grid-cols-[1fr_auto] md:items-center md:p-8"
      >
        <div className="max-w-[620px]">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-protect-soft px-2.5 py-1 text-xs font-semibold text-protect">
            <Sparkles className="size-3.5" />
            {t("how.free.badge")}
          </span>
          <h3 className="mt-3 text-xl font-semibold tracking-[-0.02em]">{t("how.free.title")}</h3>
          <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
            {t("how.free.body")}
          </p>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {["netflix@", "shop@"].map((local) => (
              <span
                key={local}
                className="max-w-full truncate rounded-full bg-muted px-2.5 py-1 font-mono text-xs"
              >
                {local}
                {SHARED_EXAMPLE}
              </span>
            ))}
          </div>
        </div>
        <Button asChild size="lg" variant="outline" className="justify-self-start">
          <Link
            to="/signup"
            onClick={() => trackEvent("signup_cta_click", { location: "how_it_works" })}
          >
            {t("how.free.cta")}
            <ArrowRight />
          </Link>
        </Button>
      </div>
    </section>
  );
}
